/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Card,
  Grid,
  Page,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Link,
  Icon,
  Tooltip as PolarisTooltip,
  Badge,
} from "@shopify/polaris";
import {
  AlertTriangleIcon,
  InfoIcon,
  RefreshIcon,
} from "@shopify/polaris-icons";
import DateRange from "../components/atoms/DateRange";
import { useFetchDashboardAnalyticsQuery } from "../store/store/slices/DashboardRTK";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";

// Shopify Polaris Colors
const COLORS = {
  FULFILLED: "#A8E6CF", // Light green
  INPROCESS: "#ADD8E6", // Light blue
  DELIVERED: "#DDA0DD", // Purple (plum)
  CANCELLED: "#FFB3BA", // Light red
  RTS: "#FF6B6B", // Red
  LEOPARD: "#5c6ac4", // Info blue
  MNP: "#5c6ac4", // Info blue
  POSTEX: "#ffc453", // Warning yellow
  PAID: "#008060", // Success green
  UNPAID: "#d72c0d", // Critical red
};

// Colors for "Orders by booked by" chart - shades of #84CBEA and #13ACF0
const BOOKED_BY_COLORS = [
  "#13ACF0", // Primary blue
  "#4BB8E8", // Medium blue
  "#84CBEA", // Light blue
  "#5DB9E6", // Medium-light blue
  "#2AB3ED", // Medium-dark blue
  "#6FC3E9", // Light-medium blue
  "#1FA5EB", // Dark blue
  "#7BC8EB", // Very light blue
  "#3AB5E9", // Medium blue variant
  "#9DD4ED", // Lightest blue
];

// Shopify Polaris color palette
const POLARIS_COLORS = {
  primary: "#008060", // Success/Primary green
  info: "#5c6ac4", // Info blue
  critical: "#d72c0d", // Critical red
  warning: "#ffc453", // Warning yellow
  subduedBg: "#f6f6f7", // Subdued background
  border: "#e1e3e5", // Border color
  textSubdued: "#6d7175", // Subdued text
  surface: "#ffffff", // Surface white
};

const Dashboard = () => {
  const navigateTo = useNavigate();
  const params = new URLSearchParams(window.location.search);

  // Calculate day-based default range for DateRange component
  // Uses the current day of the week: endDate is previous same day, startDate is 2 weeks before that
  const dayBasedDefaultRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // End date: Previous occurrence of the same day of the week (always 7 days ago)
    // If today is Monday → last Monday (7 days ago)
    // If today is Tuesday → last Tuesday (7 days ago)
    // etc.
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - 7);
    endDate.setHours(0, 0, 0, 0);

    // Start date: 2 occurrences before the end date (go back 14 days from end date = 21 days from today)
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 14);
    startDate.setHours(0, 0, 0, 0);

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDayName = dayNames[today.getDay()];

    return {
      alias: "last2weeks",
      title: `Last 2 weeks (${currentDayName}-based)`,
      period: {
        since: startDate,
        until: endDate,
      },
    };
  }, []);

  // Get dates from params, format them for API (YYYY-MM-DD)
  const getBookingFrom = () => {
    const bookingFrom = params.get("bookingfrom");
    if (bookingFrom) {
      return dayjs(new Date(bookingFrom)).format("YYYY-MM-DD");
    }
    // Fallback to 7 days ago
    return dayjs().subtract(6, "day").format("YYYY-MM-DD");
  };

  const getBookingTo = () => {
    const bookingTo = params.get("bookingto");
    if (bookingTo) {
      return dayjs(new Date(bookingTo)).format("YYYY-MM-DD");
    }
    // Fallback to today
    return dayjs().format("YYYY-MM-DD");
  };

  const {
    data: analytics,
    isError,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useFetchDashboardAnalyticsQuery<any>({
    bookingFrom: getBookingFrom(),
    bookingTo: getBookingTo(),
    deliveredFrom: params.get("deliveredfrom") ? dayjs(new Date(params.get("deliveredfrom")!)).format("YYYY-MM-DD") : "",
    deliveredTo: params.get("deliveredto") ? dayjs(new Date(params.get("deliveredto")!)).format("YYYY-MM-DD") : "",
  });

  // Format orders over time data
  const ordersOverTimeData = useMemo(() => {
    if (!analytics?.ordersOverTime) return [];
    return analytics.ordersOverTime.map((item: any) => ({
      date: dayjs(item.date).format("MMM D"),
      orders: item.count || 0,
    }));
  }, [analytics]);

  // Format average delivery days over time
  const avgDeliveryDaysData = useMemo(() => {
    if (!analytics?.avgDeliveryDaysOverTime) return [];
    return analytics.avgDeliveryDaysOverTime.map((item: any) => ({
      date: dayjs(item.date).format("MMM D"),
      days: item.avgDays || 0,
    }));
  }, [analytics]);

  // Format orders by status for bar chart
  const ordersByStatusData = useMemo(() => {
    if (!analytics?.byStatus) return [];
    const total = Object.values(analytics.byStatus).reduce((sum: number, val: any) => sum + (val || 0), 0);
    return [
      { name: "FULFILLED", value: analytics.byStatus.FULFILLED || 0, percent: total > 0 ? ((analytics.byStatus.FULFILLED || 0) / total * 100) : 0 },
      { name: "INPROCESS", value: analytics.byStatus.INPROCESS || 0, percent: total > 0 ? ((analytics.byStatus.INPROCESS || 0) / total * 100) : 0 },
      { name: "DELIVERED", value: analytics.byStatus.DELIVERED || 0, percent: total > 0 ? ((analytics.byStatus.DELIVERED || 0) / total * 100) : 0 },
      { name: "CANCELLED", value: analytics.byStatus.CANCELLED || 0, percent: total > 0 ? ((analytics.byStatus.CANCELLED || 0) / total * 100) : 0 },
      { name: "RTS", value: analytics.byStatus.RTS || 0, percent: total > 0 ? ((analytics.byStatus.RTS || 0) / total * 100) : 0 },
    ].filter((item) => item.value > 0);
  }, [analytics]);

  // Format orders by status for pie chart (Orders Breakdown)
  const ordersBreakdownPieData = useMemo(() => {
    if (!analytics?.byStatus) return [];
    return [
      { name: "FULFILLED", value: analytics.byStatus.FULFILLED || 0 },
      { name: "INPROCESS", value: analytics.byStatus.INPROCESS || 0 },
      { name: "DELIVERED", value: analytics.byStatus.DELIVERED || 0 },
      { name: "CANCELLED", value: analytics.byStatus.CANCELLED || 0 },
      { name: "RTS", value: analytics.byStatus.RTS || 0 },
    ].filter((item) => item.value > 0);
  }, [analytics]);

  // Format orders by courier for donut chart
  const ordersByCourierData = useMemo(() => {
    if (!analytics?.byCourier) return [];
    return [
      { name: "LEOPARD", value: analytics.byCourier.LEOPARD || 0 },
      { name: "MNP", value: analytics.byCourier.MNP || 0 },
      { name: "POSTEX", value: analytics.byCourier.POSTEX || 0 },
    ].filter((item) => item.value > 0);
  }, [analytics]);


  // Format orders by destination (top 10)
  const ordersByDestinationData = useMemo(() => {
    if (!analytics?.byDestination) return [];
    return Object.entries(analytics.byDestination)
      .map(([name, value]: [string, any]) => ({
        name: name.length > 20 ? name.substring(0, 20) + "..." : name,
        orders: value || 0,
      }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 10);
  }, [analytics]);

  // Format orders by booked by (top 10)
  const ordersByBookedByData = useMemo(() => {
    if (!analytics?.byBookedBy) return [];
    return Object.entries(analytics.byBookedBy)
      .map(([name, value]: [string, any]) => ({
        name: name.length > 25 ? name.substring(0, 25) + "..." : name,
        orders: value || 0,
      }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 10);
  }, [analytics]);

  // Calculate total orders from status breakdown
  // Total orders = FULFILLED + INPROCESS + DELIVERED + RTS (excludes CANCELLED)
  const totalOrders = useMemo(() => {
    if (!analytics?.byStatus) return 0;
    return (
      (analytics.byStatus.FULFILLED || 0) +
      (analytics.byStatus.INPROCESS || 0) +
      (analytics.byStatus.DELIVERED || 0) +
      (analytics.byStatus.RTS || 0)
    );
  }, [analytics]);

  // Calculate orders fulfilled (FULFILLED + DELIVERED)
  const ordersFulfilled = useMemo(() => {
    if (!analytics?.byStatus) return 0;
    return (analytics.byStatus.FULFILLED || 0);
  }, [analytics]);

  // Calculate orders delivered
  const ordersDelivered = useMemo(() => {
    if (!analytics?.byStatus) return 0;
    return analytics.byStatus.DELIVERED || 0;
  }, [analytics]);

  // Calculate delivery ratio
  // Formula: (DELIVERED / Total Orders) × 100
  // Where Total Orders = FULFILLED + INPROCESS + DELIVERED + RTS
  const deliveryRatio = useMemo(() => {
    if (!totalOrders || totalOrders === 0) return 0;
    return (ordersDelivered / totalOrders) * 100;
  }, [ordersDelivered, totalOrders]);

  // Calculate RTS ratio
  // Formula: (RTS / Total Orders) × 100
  // Where Total Orders = FULFILLED + INPROCESS + DELIVERED + RTS
  const rtsRatio = useMemo(() => {
    if (!analytics?.byStatus || !totalOrders || totalOrders === 0) return 0;
    const rtsCount = analytics.byStatus.RTS || 0;
    return (rtsCount / totalOrders) * 100;
  }, [analytics, totalOrders]);

  // Format orders by delivery days for line chart
  const ordersByDeliveryDaysChartData = useMemo(() => {
    if (!analytics?.ordersByDeliveryDays) return [];
    return [...analytics.ordersByDeliveryDays]
      .sort((a: any, b: any) => a.days - b.days)
      .map((item: any) => ({
        days: item.days,
        orders: item.numberOfOrders || 0,
      }));
  }, [analytics]);

  if (isError) {
    return (
      <Page title="Analytics">
        <Banner icon={AlertTriangleIcon} tone="warning">
          {error?.data?.errors?.[0]?.msg || "Failed to load analytics"}
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      titleHidden
      fullWidth
      secondaryActions={
        <Button
          icon={RefreshIcon}
          onClick={() => refetch()}
          loading={isFetching}
          disabled={isFetching}
        >
          Refresh
        </Button>
      }
    >
      <div style={{ padding: "20px 0" }}>
        {/* Date Range Filter */}
        <div style={{ marginBottom: "20px", display: "flex", gap: "10px", flexDirection: "column" }}>
          <DateRange defaultRange={dayBasedDefaultRange} />
          <div>

            <Banner tone="info" icon={InfoIcon}>Total booked orders is the count of orders across all active statuses (Fulfilled, In Process, Delivered, and Return to Sender). Cancelled orders are excluded from this count.</Banner>
          </div>
        </div>

        <BlockStack gap="400">
          {/* First Row - 4 Cards */}
          <Grid columns={{ xs: 1, sm: 2, md: 2, lg: 4, xl: 4 }}>
            {/* Total booked orders */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "max-content" }}>
                  <BlockStack gap="200">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", marginBottom: "8px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "32px", width: "120px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Text variant="headingMd" as="h3">
                              Total booked orders
                            </Text>
                            <span>
                              <PolarisTooltip content="Total count of orders across all active statuses (Fulfilled, In Process, Delivered, and Return to Sender). Cancelled orders are excluded from this count.">
                                <Icon source={InfoIcon} />
                              </PolarisTooltip>
                            </span>
                          </div>
                        </Text>
                        <Text variant="headingXl" as="h2">
                          {totalOrders.toLocaleString()}
                        </Text>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>
            {/* Status delivered */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "max-content" }}>
                  <BlockStack gap="200">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", marginBottom: "8px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "32px", width: "120px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Text variant="headingMd" as="h3">
                              Status delivered
                            </Text>
                            <span>
                              <PolarisTooltip content="Total number of orders that have been successfully delivered to customers.">
                                <Icon source={InfoIcon} />
                              </PolarisTooltip>
                            </span>
                          </div>
                        </Text>
                        <Text variant="headingXl" as="h2">
                          {ordersDelivered.toLocaleString()}
                        </Text>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

            {/* Delivery ratio */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "max-content" }}>
                  <BlockStack gap="200">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", marginBottom: "8px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "32px", width: "120px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Text variant="headingMd" as="h3">
                              Delivery ratio
                            </Text>
                            <span>
                              <PolarisTooltip content="Percentage of successfully delivered orders relative to the total number of booked orders (excluding cancelled orders).">
                                <Icon source={InfoIcon} />
                              </PolarisTooltip>
                            </span>
                          </div>
                        </Text>
                        <Text variant="headingXl" as="h2">
                          {deliveryRatio.toFixed(1)}%
                        </Text>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

            {/* Average delivery time */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "max-content" }}>
                  <BlockStack gap="200">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", marginBottom: "8px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "32px", width: "120px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Text variant="headingMd" as="h3">
                              Avg delivery time
                            </Text>
                            <span>
                              <PolarisTooltip content="Average number of days taken from order booking to successful delivery across all delivered orders.">
                                <Icon source={InfoIcon} />
                              </PolarisTooltip>
                            </span>
                          </div>
                        </Text>
                        <Text variant="headingXl" as="h2">
                          {analytics?.averageDeliveryDays
                            ? analytics.averageDeliveryDays.toFixed(1)
                            : "0.0"} <span style={{ fontSize: "16px", }}>Days</span>

                        </Text>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>

          {/* Second Row - 4 Cards */}
          <Grid columns={{ xs: 1, sm: 2, md: 2, lg: 4, xl: 4 }}>
            {/* Status fulfilled */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "max-content" }}>
                  <BlockStack gap="200">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", marginBottom: "8px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "32px", width: "120px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Text variant="headingMd" as="h3">
                              Status Fulfilled
                            </Text>
                            <span>
                              <PolarisTooltip content="Total number of orders that have been fulfilled and are ready for processing.">
                                <Icon source={InfoIcon} />
                              </PolarisTooltip>
                            </span>
                          </div>
                        </Text>
                        <Text variant="headingXl" as="h2">
                          {ordersFulfilled.toLocaleString()}
                        </Text>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

            {/* Status inprocess */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "max-content" }}>
                  <BlockStack gap="200">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", marginBottom: "8px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "32px", width: "120px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Text variant="headingMd" as="h3">
                              Status Inprocess
                            </Text>
                            <span>
                              <PolarisTooltip content="Total number of orders currently being processed and prepared for shipment.">
                                <Icon source={InfoIcon} />
                              </PolarisTooltip>
                            </span>
                          </div>
                        </Text>
                        <Text variant="headingXl" as="h2">
                          {(analytics?.byStatus?.INPROCESS || 0).toLocaleString()}
                        </Text>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

            {/* Status RTS */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "max-content" }}>
                  <BlockStack gap="200">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", marginBottom: "8px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "32px", width: "120px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Text variant="headingMd" as="h3">
                              Status RTS
                            </Text>
                            <span>
                              <PolarisTooltip content="Total number of orders that were returned to the sender and could not be delivered to the intended recipient.">
                                <Icon source={InfoIcon} />
                              </PolarisTooltip>
                            </span>
                          </div>
                        </Text>
                        <Text variant="headingXl" as="h2">
                          {(analytics?.byStatus?.RTS || 0).toLocaleString()}
                        </Text>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

            {/* RTS ratio */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "max-content" }}>
                  <BlockStack gap="200">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", marginBottom: "8px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "32px", width: "120px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Text variant="headingMd" as="h3">
                              RTS ratio
                            </Text>
                            <span>
                              <PolarisTooltip content="Percentage of orders returned to sender (RTS) relative to the total number of booked orders (excluding cancelled orders).">
                                <Icon source={InfoIcon} />
                              </PolarisTooltip>
                            </span>
                          </div>
                        </Text>
                        <Text variant="headingXl" as="h2">
                          {rtsRatio.toFixed(1)}%
                        </Text>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>

          {/* Main Charts Row */}
          <Grid columns={{ xs: 1, md: 1, lg: 2, xl: 2 }}>
            {/* Orders Over Time */}
            {/* <Grid.Cell>
              <Card>
                <div style={{ minHeight: "300px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper" style={{ minHeight: "336px" }}>
                        <div className="dashboard-skeleton" style={{ height: "20px", width: "180px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "300px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Total orders over time
                        </Text>
                        {ordersOverTimeData.length > 0 ? (
                          <div style={{ height: "260px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={ordersOverTimeData}>
                                <defs>
                                  <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={POLARIS_COLORS.info} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={POLARIS_COLORS.info} stopOpacity={0} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={POLARIS_COLORS.border} />
                                <XAxis
                                  dataKey="date"
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                  axisLine={{ stroke: POLARIS_COLORS.border }}
                                />
                                <YAxis
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                  axisLine={{ stroke: POLARIS_COLORS.border }}
                                />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: POLARIS_COLORS.surface,
                                    border: `1px solid ${POLARIS_COLORS.border}`,
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                />
                                <Area
                                  type="monotone"
                                  dataKey="orders"
                                  stroke={POLARIS_COLORS.info}
                                  fill="url(#colorOrders)"
                                  strokeWidth={2}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div style={{ height: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Text tone="subdued" as="p">No data available</Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell> */}
            {/* order status chart - pie chart */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "305px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "20px", width: "160px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "250px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Orders by status
                        </Text>
                        {ordersByStatusData.length > 0 ? (
                          <div style={{ height: "250px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={ordersByStatusData}
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke={POLARIS_COLORS.border} />
                                <XAxis type="number" domain={[0, 100]} tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }} />
                                <YAxis
                                  dataKey="name"
                                  type="category"
                                  width={100}
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                />
                                <Tooltip
                                  formatter={(value: number) => [`${value.toFixed(1)}%`, "Percentage"]}
                                  contentStyle={{
                                    backgroundColor: POLARIS_COLORS.surface,
                                    border: `1px solid ${POLARIS_COLORS.border}`,
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                />
                                <Bar
                                  dataKey="percent"
                                  radius={[0, 4, 4, 0]}
                                  label={{ position: "right", fill: "#000000", fontSize: 12, formatter: (value: number) => `${value.toFixed(1)}%` }}
                                >
                                  {ordersByStatusData.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[entry.name as keyof typeof COLORS] || POLARIS_COLORS.info}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div style={{ height: "250px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Text tone="subdued" as="p">No data available</Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>
            {/* Orders Breakdown - Pie Chart */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "300px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "20px", width: "160px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "250px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Orders breakdown
                        </Text>
                        {ordersBreakdownPieData.length > 0 ? (
                          <div style={{ height: "270px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={ordersBreakdownPieData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={true}
                                  label={({ name, value }) => {
                                    // Show status name and parcel count on the chart
                                    return `${name}\n${value.toLocaleString()}`;
                                  }}
                                  outerRadius={110}
                                  innerRadius={50}
                                  paddingAngle={3}
                                  dataKey="value"
                                  stroke={POLARIS_COLORS.surface}
                                  strokeWidth={2}
                                  isAnimationActive={true}
                                  animationBegin={0}
                                  animationDuration={800}
                                >
                                  {ordersBreakdownPieData.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[entry.name as keyof typeof COLORS] || POLARIS_COLORS.info}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  formatter={(value: number, _name: string, props: any) => {
                                    const total = ordersBreakdownPieData.reduce((sum, item) => sum + item.value, 0);
                                    const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                    return [
                                      `${value.toLocaleString()} parcels (${percent}%)`,
                                      props.payload.name,
                                    ];
                                  }}
                                  contentStyle={{
                                    backgroundColor: POLARIS_COLORS.surface,
                                    border: `1px solid ${POLARIS_COLORS.border}`,
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div style={{ height: "280px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Text tone="subdued" as="p">No data available</Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>

          {/* Secondary Charts Row */}
          <Grid columns={{ xs: 1, md: 2, lg: 3, xl: 3 }}>
            {/* Orders by Status (Donut) */}
            {/* <Grid.Cell>
              <Card>
                <div style={{ minHeight: "330px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "20px", width: "160px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "250px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Orders by status
                        </Text>
                        {ordersByStatusData.length > 0 ? (
                          <div style={{ height: "250px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={ordersByStatusData}
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke={POLARIS_COLORS.border} />
                                <XAxis type="number" domain={[0, 100]} tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }} />
                                <YAxis
                                  dataKey="name"
                                  type="category"
                                  width={100}
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                />
                                <Tooltip
                                  formatter={(value: number) => [`${value.toFixed(1)}%`, "Percentage"]}
                                  contentStyle={{
                                    backgroundColor: POLARIS_COLORS.surface,
                                    border: `1px solid ${POLARIS_COLORS.border}`,
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                />
                                <Bar
                                  dataKey="percent"
                                  radius={[0, 4, 4, 0]}
                                  label={{ position: "right", fill: "#000000", fontSize: 12, formatter: (value: number) => `${value.toFixed(1)}%` }}
                                >
                                  {ordersByStatusData.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[entry.name as keyof typeof COLORS] || POLARIS_COLORS.info}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div style={{ height: "250px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Text tone="subdued" as="p">No data available</Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell> */}

            {/* Orders by Courier (Donut) */}
            {/* <Grid.Cell>
              <Card>
                <div style={{ minHeight: "330px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "20px", width: "160px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "250px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Orders by courier
                        </Text>
                        {ordersByCourierData.length > 0 ? (
                          <div style={{ height: "250px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={ordersByCourierData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) =>
                                    `${name}: ${(percent * 100).toFixed(0)}%`
                                  }
                                  outerRadius={80}
                                  fill={POLARIS_COLORS.info}
                                  dataKey="value"
                                >
                                  {ordersByCourierData.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[entry.name as keyof typeof COLORS] || POLARIS_COLORS.info}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div style={{ height: "250px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Text tone="subdued" as="p">No data available</Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell> */}

            {/* Average Delivery Days Over Time */}
            {/* <Grid.Cell>
              <Card>
                <div style={{ minHeight: "330px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "20px", width: "160px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "250px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Average delivery days over time
                        </Text>
                        {avgDeliveryDaysData.length > 0 ? (
                          <div style={{ height: "250px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={avgDeliveryDaysData}>
                                <CartesianGrid strokeDasharray="3 3" stroke={POLARIS_COLORS.border} />
                                <XAxis
                                  dataKey="date"
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                  axisLine={{ stroke: POLARIS_COLORS.border }}
                                />
                                <YAxis
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                  axisLine={{ stroke: POLARIS_COLORS.border }}
                                />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: POLARIS_COLORS.surface,
                                    border: `1px solid ${POLARIS_COLORS.border}`,
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="days"
                                  stroke={POLARIS_COLORS.info}
                                  strokeWidth={2}
                                  dot={{ fill: POLARIS_COLORS.info, r: 4 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div style={{ height: "250px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Text tone="subdued" as="p">No data available</Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell> */}
            <Grid.Cell columnSpan={{ xs: 3, md: 2, lg: 2, xl: 1 }}>
              <Card>
                <div style={{ minHeight: "300px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper" style={{ minHeight: "336px" }}>
                        <div
                          style={{
                            borderBottom: `1px dotted ${POLARIS_COLORS.border}`,
                            paddingBottom: "8px",
                            marginBottom: "16px",
                          }}
                        >
                          <div className="dashboard-skeleton" style={{ height: "20px", width: "200px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        </div>
                        <BlockStack gap="200">
                          {Array(10).fill(0).map((_, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
                              <div className="dashboard-skeleton" style={{ height: "16px", width: "150px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                              <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                            </div>
                          ))}
                        </BlockStack>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            paddingBottom: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Text variant="headingMd" as="h3">
                            Orders delivered by days
                          </Text>
                        </div>
                        <BlockStack gap="0">
                          {analytics?.ordersByDeliveryDays && analytics.ordersByDeliveryDays.length > 0 ? (
                            analytics.ordersByDeliveryDays.map((item: any, index: number) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "8px 0",
                                  borderBottom: index < analytics.ordersByDeliveryDays.length - 1 ? `1px solid ${POLARIS_COLORS.border}` : "none",
                                }}
                              >
                                <Text variant="bodyMd" as="span">
                                  Delivered in {item.days === 10 ? item.days + "+" : item.days}  {item.days === 1 ? "day" : "days"}
                                </Text>
                                <InlineStack align="center" gap="200">
                                  <Text variant="bodyMd" fontWeight="semibold" as="span">
                                    {item.numberOfOrders.toLocaleString()}
                                  </Text>
                                  <Link
                                    url={`/dashboard/orders?status=DELIVERED&days=${item.days}&op=eq&bookingfrom=${params.get("bookingfrom")}&bookingto=${params.get("bookingto")}`}
                                  >
                                    <Text
                                      variant="bodyMd"
                                      as="span"
                                      tone="info"
                                      style={{
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                      }}
                                    >
                                      show orders
                                    </Text>
                                  </Link>
                                </InlineStack>
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: "20px 0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Text tone="subdued" as="p">No data available</Text>
                            </div>
                          )}
                        </BlockStack>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

            {/* Orders by Delivery Days Line Chart */}
            <Grid.Cell columnSpan={{ xs: 3, md: 2, lg: 2, xl: 2 }}>
              <Card>
                <div style={{ minHeight: "425px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper" style={{ minHeight: "336px" }}>
                        <div className="dashboard-skeleton" style={{ height: "20px", width: "200px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "250px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Orders by Delivery Days
                        </Text>
                        {ordersByDeliveryDaysChartData.length > 0 ? (
                          <div style={{ height: "330px", marginTop: "20px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={ordersByDeliveryDaysChartData}>
                                <defs>
                                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#5CB3E0" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="#5CB3E0" stopOpacity={0.05} />
                                  </linearGradient>
                                  <filter id="lineShadow" x="-50%" y="-50%" width="200%" height="200%">
                                    <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
                                    <feOffset dx="0" dy="2" result="offsetblur" />
                                    <feComponentTransfer>
                                      <feFuncA type="linear" slope="0.3" />
                                    </feComponentTransfer>
                                    <feMerge>
                                      <feMergeNode />
                                      <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                  </filter>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={POLARIS_COLORS.border} opacity={0.5} />
                                <XAxis
                                  dataKey="days"
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                  axisLine={{ stroke: POLARIS_COLORS.border }}
                                  label={{ value: "Number of Days", position: "insideBottom", offset: -5, fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                />
                                <YAxis
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                  axisLine={{ stroke: POLARIS_COLORS.border }}
                                  label={{ value: "Number of Orders", angle: -90, position: "insideLeft", fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: POLARIS_COLORS.surface,
                                    border: `1px solid ${POLARIS_COLORS.border}`,
                                    borderRadius: "8px",
                                    padding: "12px 16px",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                  }}
                                  cursor={{ stroke: "#5CB3E0", strokeWidth: 2, strokeDasharray: "5 5" }}
                                  formatter={(value: number) => [`${value.toLocaleString()}`, "Orders"]}
                                  labelFormatter={(label) => `Day ${label}`}
                                />
                                <Area
                                  type="monotone"
                                  dataKey="orders"
                                  stroke="none"
                                  fill="url(#areaGradient)"
                                  activeDot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="orders"
                                  stroke="#5CB3E0"
                                  strokeWidth={3}
                                  dot={false}
                                  activeDot={{
                                    r: 7,
                                    fill: "#5CB3E0",
                                    strokeWidth: 3,
                                    stroke: "#fff",
                                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))"
                                  }}
                                  filter="url(#lineShadow)"
                                  cursor="pointer"
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div style={{ height: "250px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Text tone="subdued" as="p">No data available</Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

          </Grid>

          {/* Bottom Charts Row */}
          <Grid columns={{ xs: 1, md: 1, lg: 3, xl: 2 }}>
            {/* Orders by Destination */}
            {/* <Grid.Cell>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h3">
                  Orders by destination city
                </Text>
                {isLoading || isFetching ? (
                  <div className="dashboard-skeleton-wrapper" style={{ height: "300px" }}>
                    <div className="dashboard-skeleton" style={{ height: "32px", width: "200px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                  </div>
                ) : ordersByDestinationData.length > 0 ? (
                  <div style={{ height: "300px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ordersByDestinationData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={POLARIS_COLORS.border} />
                        <XAxis type="number" tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={120}
                          tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: POLARIS_COLORS.surface,
                            border: `1px solid ${POLARIS_COLORS.border}`,
                            borderRadius: "6px",
                            padding: "8px 12px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Bar dataKey="orders" fill={POLARIS_COLORS.info} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div style={{ height: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Text tone="subdued" as="p">No data available</Text>
                  </div>
                )}
              </BlockStack>
            </Card>
          </Grid.Cell> */}

            {/* Orders by Booked By */}
            {/* <Grid.Cell>
              <Card>
                <div style={{ minHeight: "380px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div className="dashboard-skeleton" style={{ height: "20px", width: "180px", marginBottom: "16px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        <div className="dashboard-skeleton" style={{ height: "300px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Orders by booked by
                        </Text>
                        {ordersByBookedByData.length > 0 ? (
                          <div style={{ height: "300px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={ordersByBookedByData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={POLARIS_COLORS.border} />
                                <XAxis type="number" tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }} />
                                <YAxis
                                  dataKey="name"
                                  type="category"
                                  width={150}
                                  tick={{ fill: POLARIS_COLORS.textSubdued, fontSize: 12 }}
                                />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: POLARIS_COLORS.surface,
                                    border: `1px solid ${POLARIS_COLORS.border}`,
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                />
                                <Bar dataKey="orders" radius={[0, 4, 4, 0]}>
                                  {ordersByBookedByData.map((_, index: number) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={BOOKED_BY_COLORS[index % BOOKED_BY_COLORS.length]}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div style={{ height: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Text tone="subdued" as="p">No data available</Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell> */}

            {/* <Grid columns={{ xs: 1, md: 1, lg: 1, xl: 1 }}>
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "300px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper" style={{ minHeight: "336px" }}>
                        <div
                          style={{
                            borderBottom: `1px dotted ${POLARIS_COLORS.border}`,
                            paddingBottom: "8px",
                            marginBottom: "16px",
                          }}
                        >
                          <div className="dashboard-skeleton" style={{ height: "20px", width: "200px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                        </div>
                        <BlockStack gap="200">
                          {Array(10).fill(0).map((_, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
                              <div className="dashboard-skeleton" style={{ height: "16px", width: "150px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                              <div className="dashboard-skeleton" style={{ height: "16px", width: "100px", borderRadius: "4px", backgroundColor: POLARIS_COLORS.subduedBg }} />
                            </div>
                          ))}
                        </BlockStack>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            paddingBottom: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Text variant="headingMd" as="h3">
                            <span style={{ borderBottom: `2px dotted ${POLARIS_COLORS.border}`, paddingBottom: "2px", color: 'black' }}>
                              Orders delivered by days
                            </span>
                          </Text>
                        </div>
                        <BlockStack gap="0">
                          {analytics?.ordersByDeliveryDays && analytics.ordersByDeliveryDays.length > 0 ? (
                            analytics.ordersByDeliveryDays.map((item: any, index: number) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "8px 0",
                                  borderBottom: index < analytics.ordersByDeliveryDays.length - 1 ? `1px solid ${POLARIS_COLORS.border}` : "none",
                                }}
                              >
                                <Text variant="bodyMd" as="span">
                                  Delivered in {item.days}  {item.days === 1 ? "day" : "days"}
                                </Text>
                                <InlineStack align="center" gap="200">
                                  <Text variant="bodyMd" fontWeight="semibold" as="span">
                                    {item.numberOfOrders.toLocaleString()}
                                  </Text>
                                  <Link
                                    url={`/dashboard/orders?status=DELIVERED&days=${item.days}&op=eq&bookingfrom=${params.get("bookingfrom")}&bookingto=${params.get("bookingto")}`}
                                  >
                                    <Text
                                      variant="bodyMd"
                                      as="span"
                                      tone="info"
                                      style={{
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                      }}
                                    >
                                      show orders
                                    </Text>
                                  </Link>
                                </InlineStack>
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: "20px 0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Text tone="subdued" as="p">No data available</Text>
                            </div>
                          )}
                        </BlockStack>
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>
          </Grid> */}
          </Grid>


        </BlockStack>
      </div>
    </Page>
  );
};

export default Dashboard;
